import { PlatformAppUiSchema } from '@typings';
import { isSchemaAnyOf, isSchemaArray, isSchemaContainer } from '@utils/guards';

import { Render } from '@components/Shared';

import { PlatformAppsComponentMap } from './PlatformAppsComponentMap';
import { PlatformAppUiSchemaAnyOf } from './PlatformAppUiSchemaAnyOf';
import { PlatformAppUiSchemaArray } from './PlatformAppUiSchemaArray';

type Props = {
  withFormatter?: boolean;
  schema: PlatformAppUiSchema.Model;
};

export const PlatformAppsUiSchema: React.FC<Props> = ({
  withFormatter,
  schema,
}) => {
  const makeElement = (
    model: PlatformAppUiSchema.Model,
    /**
     * Only direct children components should not be formatted
     */
    withFormatter?: boolean,
  ) => {
    if (isSchemaContainer(model)) {
      const { title, description, children } = model;

      return (
        <div
          key={title || description}
          className="rounded-xl border border-neural-02 p-6"
        >
          <p className="flex items-start text-h5">{title}</p>
          <Render if={description}>
            <p className="mt-0.5 text-footnote text-neural-04">{description}</p>
          </Render>
          <div className="mt-4 flex flex-col gap-6">
            {children.map((node) => makeElement(node))}
          </div>
        </div>
      );
    }

    if (isSchemaAnyOf(model)) {
      return <PlatformAppUiSchemaAnyOf key={model.name} schema={model} />;
    }

    if (isSchemaArray(model)) {
      return <PlatformAppUiSchemaArray key={model.name} schema={model} />;
    }

    const {
      typeName,
      properties,
      properties: { name },
    } = model;

    return (
      <PlatformAppsComponentMap
        withFormatter={withFormatter}
        key={name}
        name={name}
        typeName={typeName}
        properties={properties}
      />
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {makeElement(schema, withFormatter)}
    </div>
  );
};
